import { type CardProps } from 'antd';
import dayjs from 'dayjs';
import { head, isEmpty, isNil } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '../../../../../components/contexts/CompanyContextManager';
import getExpirationDuration from '../../../../../utilities/journeys/getExpirationDuration';
import useCompanyFormats from '../../../../../utilities/useCompanyFormats';
import { usePersistentData, usePersistentDataListener } from '../../../../../utilities/usePersistData';
import usePublic from '../../../../../utilities/usePublic';
import useTranslatedString from '../../../../../utilities/useTranslatedString';
import { StyledVehicleCard, StyledVehicleCardMeta } from '../../../../shared/CIPage/carListingUI';
import {
    type MobilityJourneyTemporaryValueType,
    type TemporaryMobilityBookingDetails,
    mobilityJourneyTemporaryKey,
    retrieveMobilityJourneyValue,
} from '../../helper';
import { type MobilityStock, useCarListingState } from '../CarListingStateProvider';

export type CarCardProps = Omit<CardProps, 'onClick'> & {
    stock: MobilityStock;
};

const CarCard = ({ stock, ...props }: CarCardProps) => {
    const { t } = useTranslation(['carList']);
    const fallbackSrc = usePublic('empty.svg');
    const translate = useTranslatedString();

    const {
        state: { rentalPeriod, startTime, endTime },
        meta: { endpoint },
    } = useCarListingState();

    const { setItem, getSessionId } = usePersistentData();
    const { sessionTimeout } = useCompany();

    const mobilityJourneyTemporaryValue =
        usePersistentDataListener<MobilityJourneyTemporaryValueType>(mobilityJourneyTemporaryKey);

    const navigate = useNavigate();

    const { formatAmountWithCurrency } = useCompanyFormats();

    const [image, available, stockCarPrice] = useMemo(() => {
        if (
            stock.__typename !== 'MobilityStockInventory' ||
            stock.inventory.__typename !== 'MobilityInventory' ||
            stock.inventory.variant.__typename !== 'LocalVariant'
        ) {
            return [];
        }

        const image = head(stock.images)?.url || head(stock.inventory.variant.images)?.url || fallbackSrc;

        const from = rentalPeriod?.start ? dayjs(rentalPeriod?.start) : null;
        const to = rentalPeriod?.end ? dayjs(rentalPeriod?.end) : null;

        const start = startTime ? dayjs(startTime) : null;
        const end = endTime ? dayjs(endTime) : null;
        const stockCarPrice = stock.price || stock.inventory.variant.vehiclePrice;
        // from and/or to is not set, will not block the selection
        if (
            isNil(from) ||
            isNil(to) ||
            isNil(start) ||
            isNil(end) ||
            !from?.isValid() ||
            !to.isValid() ||
            !start.isValid() ||
            !end.isValid()
        ) {
            return [image, true, stockCarPrice];
        }

        // check for reservations
        const hasReservation = stock.reservations.some(
            reservation =>
                dayjs(reservation.bookingPeriod.start)
                    .subtract(endpoint.mobilityApplicationModule.durationBeforeNextBooking, 'hour')
                    .isBetween(from, to) ||
                dayjs(reservation.bookingPeriod.end)
                    .add(endpoint.mobilityApplicationModule.durationBeforeNextBooking, 'hour')
                    .isBetween(from, to)
        );

        const hasBlockPeriod = stock.blockPeriod.some(block => {
            const updatedStartBlock = dayjs(block.start).subtract(
                endpoint.mobilityApplicationModule.durationBeforeNextBooking,
                'hour'
            );
            const updatedEnd = dayjs(block.end).add(
                endpoint.mobilityApplicationModule.durationBeforeNextBooking,
                'hour'
            );

            return (
                dayjs(from).isBetween(updatedStartBlock, updatedEnd) ||
                dayjs(to).isBetween(updatedStartBlock, updatedEnd)
            );
        });

        // check for available period, prefer settings on stocks over inventories
        const period = stock.period || stock.inventory.period;
        const isWithinAvailablePeriod =
            isEmpty(period) || (from.isSameOrAfter(period.start, 'day') && to.isSameOrBefore(period.end, 'day'));

        return [image, !hasReservation && !hasBlockPeriod && isWithinAvailablePeriod, stockCarPrice];
    }, [
        stock,
        fallbackSrc,
        rentalPeriod?.start,
        rentalPeriod?.end,
        startTime,
        endTime,
        endpoint.mobilityApplicationModule.durationBeforeNextBooking,
    ]);

    const onClick = useCallback(() => {
        if (available) {
            const rentalPeriodStart = rentalPeriod && dayjs(rentalPeriod.start).toDate();
            const rentalPeriodEnd = rentalPeriod && dayjs(rentalPeriod.end).toDate();
            const newBookingDetails: TemporaryMobilityBookingDetails = {
                period: {
                    start: rentalPeriodStart,
                    end: rentalPeriodEnd,
                    startTime,
                    endTime,
                },
                inventoryStockId: stock.id,
                location: null,
            };
            if (mobilityJourneyTemporaryValue?.bookingDetails) {
                let updatedTemporaryValue: MobilityJourneyTemporaryValueType;
                if (mobilityJourneyTemporaryValue.bookingDetails.inventoryStockId === stock.id) {
                    updatedTemporaryValue = {
                        ...mobilityJourneyTemporaryValue,
                        bookingDetails: {
                            period: {
                                start: rentalPeriodStart || mobilityJourneyTemporaryValue.bookingDetails?.period?.start,
                                end: rentalPeriodEnd || mobilityJourneyTemporaryValue.bookingDetails?.period?.end,
                                startTime: startTime || mobilityJourneyTemporaryValue.bookingDetails?.period?.startTime,
                                endTime: endTime || mobilityJourneyTemporaryValue.bookingDetails?.period?.endTime,
                            },
                            inventoryStockId: stock.id,
                            location: mobilityJourneyTemporaryValue.bookingDetails?.location,
                        },
                    };
                } else {
                    updatedTemporaryValue = {
                        ...mobilityJourneyTemporaryValue,
                        bookingDetails: newBookingDetails,
                    };
                }
                const { sessionId, value } = retrieveMobilityJourneyValue(updatedTemporaryValue);

                if (updatedTemporaryValue.token && updatedTemporaryValue.expireAt) {
                    setItem(
                        mobilityJourneyTemporaryKey,
                        value,
                        getExpirationDuration(updatedTemporaryValue.expireAt),
                        sessionId
                    );
                } else {
                    setItem(mobilityJourneyTemporaryKey, value, sessionTimeout * 60, sessionId);
                }
            } else {
                setItem(
                    mobilityJourneyTemporaryKey,
                    { bookingDetails: newBookingDetails },
                    sessionTimeout * 60,
                    getSessionId(mobilityJourneyTemporaryKey)
                );
            }

            navigate(`./details/${stock.id}`, {
                state: {
                    rentalPeriod: { start: rentalPeriod?.start, end: rentalPeriod?.end },
                    startTime,
                    endTime,
                },
            });
        }
    }, [
        available,
        rentalPeriod,
        startTime,
        endTime,
        stock.id,
        mobilityJourneyTemporaryValue,
        navigate,
        setItem,
        sessionTimeout,
        getSessionId,
    ]);

    if (
        stock.__typename !== 'MobilityStockInventory' ||
        stock.inventory.__typename !== 'MobilityInventory' ||
        stock.inventory.variant.__typename !== 'LocalVariant'
    ) {
        return null;
    }

    return (
        <StyledVehicleCard
            key={stock.id}
            bordered={false}
            cover={<img alt={stock.id} src={image} />}
            disabled={!available}
            hoverable={false}
            onClick={available ? onClick : null}
            {...props}
        >
            <StyledVehicleCardMeta
                description={t('carList:carPricePerDay', {
                    amount: formatAmountWithCurrency(stockCarPrice),
                })}
                title={translate(stock.inventory.variant.name)}
            />
        </StyledVehicleCard>
    );
};

export default CarCard;
